import React, { useState } from 'react';
import { markComplete, approveAllReviews } from '../../api/questionnaire';
import { ConfirmationModal } from '../ui/Modal';
import ReopenQuestionnaireModal from './ReopenQuestionnaireModal';
import ChangeProposalPanel from './ChangeProposalPanel';
import { useToast } from '../../hooks/useToast';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * CompletionDashboard - Shows questionnaire completion status and metrics
 */
const CompletionDashboard = ({ completionStatus, leadId, onComplete, onRefresh, latestProposalStatus, activeFilter, onFilterChange }) => {
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState(null);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [showProposalPanel, setShowProposalPanel] = useState(false);
  const [showApproveAllConfirm, setShowApproveAllConfirm] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);
  const toast = useToast();
  const { can } = usePermissions();

  if (!completionStatus) {
    return null;
  }

  const {
    status,
    completion_percentage,
    answered_count,
    pending_count,
    needs_review_count,
    pending_proposals_count = 0,
    average_confidence,
    can_complete,
    blocking_issues
  } = completionStatus;

  const handleMarkComplete = async () => {
    setShowCompleteConfirm(false);
    try {
      setMarking(true);
      setError(null);
      const response = await markComplete(leadId, { force_complete: false });

      // Backend returns {data: {...}, meta: {...}} on success
      if (response.data) {
        toast.success('Questionnaire marked as complete!');
        onComplete && onComplete();
      } else {
        setError(response.message || 'Failed to mark as complete');
      }
    } catch (err) {
      console.error('Error marking complete:', err);
      // Handle error object from backend: {detail: {error: {message, code, details}}}
      const errorMsg = err.response?.data?.detail?.error?.message
        || err.response?.data?.detail
        || err.message
        || 'Failed to mark questionnaire as complete';
      const errorMessage = typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg);
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setMarking(false);
    }
  };



  const handleApproveAll = async () => {
    setShowApproveAllConfirm(false);
    try {
      setApprovingAll(true);
      await approveAllReviews(leadId);
      toast.success('All reviews approved');
      onRefresh && onRefresh();
    } catch (err) {
      console.error('Error approving all reviews:', err);
      const errorMsg = err.response?.data?.detail?.error?.message
        || err.response?.data?.detail
        || err.message
        || 'Failed to approve all reviews';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setApprovingAll(false);
    }
  };

  // Status badge styling
  const getStatusBadge = () => {
    const badges = {
      not_started: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Not Started' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'In Progress' },
      pending_review: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending Review' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' }
    };

    const badge = badges[status] || badges.not_started;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  // Confidence indicator
  const getConfidenceIndicator = () => {
    if (!average_confidence) return null;

    const conf = parseFloat(average_confidence);
    let color = 'text-red-600';
    let label = 'Low';

    if (conf >= 0.8) {
      color = 'text-green-600';
      label = 'High';
    } else if (conf >= 0.7) {
      color = 'text-yellow-600';
      label = 'Medium';
    }

    return (
      <div className="flex items-center space-x-1.5">
        <span className={`text-lg font-bold ${color}`}>{Math.round(conf * 100)}%</span>
        <span className="text-xs text-gray-600">{label} Confidence</span>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
      {pending_proposals_count > 0 && can.editQuestionnaire() && (
        <button
          onClick={() => setShowProposalPanel(true)}
          className="w-full flex items-center gap-2 px-3 py-2 mb-3 text-sm font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition text-left animate-attention-pulse motion-reduce:animate-none"
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Pending changes awaiting review — click to open
        </button>
      )}

      {/* Header + Progress Bar */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Completion Status</h2>
          <p className="text-xs text-gray-500">Track questionnaire progress and readiness</p>
        </div>
        {getStatusBadge()}
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-gray-700">Overall Progress</span>
          <span className="text-xs font-semibold text-gray-900">
            {Math.round(completion_percentage)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${
              completion_percentage >= 80 ? 'bg-green-500'
                : completion_percentage >= 50 ? 'bg-blue-500'
                : completion_percentage >= 20 ? 'bg-yellow-500'
                : 'bg-red-500'
            }`}
            style={{ width: `${completion_percentage}%` }}
          />
        </div>
      </div>

      {/* Clear filter — above grid, right-aligned */}
      {activeFilter && onFilterChange && (
        <div className="flex justify-end mb-2">
          <button
            onClick={() => onFilterChange(null)}
            className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition"
          >
            Clear Filter ×
          </button>
        </div>
      )}

      {/* Compact Metrics Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <div
          onClick={() => onFilterChange && onFilterChange(activeFilter === 'answered' ? null : 'answered')}
          className={`flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg transition ${
            onFilterChange ? 'cursor-pointer hover:bg-green-100' : ''
          } ${activeFilter === 'answered' ? 'ring-2 ring-green-500 ring-offset-1' : ''}`}
        >
          <span className="text-lg font-bold text-green-600">{answered_count}</span>
          <span className="text-xs text-gray-600">Answered</span>
          {activeFilter === 'answered' && (
            <svg className="w-3.5 h-3.5 text-green-500 ml-auto" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L13 10.414V15a1 1 0 01-.553.894l-4 2A1 1 0 017 17v-6.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
            </svg>
          )}
        </div>
        <div
          onClick={() => onFilterChange && onFilterChange(activeFilter === 'pending' ? null : 'pending')}
          className={`flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg transition ${
            onFilterChange ? 'cursor-pointer hover:bg-red-100' : ''
          } ${activeFilter === 'pending' ? 'ring-2 ring-red-400 ring-offset-1' : ''}`}
        >
          <span className="text-lg font-bold text-red-600">{pending_count}</span>
          <span className="text-xs text-gray-600">Pending</span>
          {activeFilter === 'pending' && (
            <svg className="w-3.5 h-3.5 text-red-500 ml-auto" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L13 10.414V15a1 1 0 01-.553.894l-4 2A1 1 0 017 17v-6.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
            </svg>
          )}
        </div>
        <div
          onClick={() => onFilterChange && onFilterChange(activeFilter === 'review' ? null : 'review')}
          className={`flex items-center gap-2 px-3 py-2 bg-yellow-50 rounded-lg transition ${
            onFilterChange ? 'cursor-pointer hover:bg-yellow-100' : ''
          } ${activeFilter === 'review' ? 'ring-2 ring-yellow-500 ring-offset-1' : ''}`}
        >
          <span className="text-lg font-bold text-yellow-600">{needs_review_count}</span>
          <span className="text-xs text-gray-600">Review</span>
          {activeFilter === 'review' && (
            <svg className="w-3.5 h-3.5 text-yellow-500 ml-auto" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L13 10.414V15a1 1 0 01-.553.894l-4 2A1 1 0 017 17v-6.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
            </svg>
          )}
          {needs_review_count > 0 && status !== 'completed' && can.completeQuestionnaire() && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowApproveAllConfirm(true); }}
              disabled={approvingAll}
              className="ml-auto px-2 py-0.5 text-[10px] font-medium text-yellow-700 bg-yellow-100 border border-yellow-300 rounded hover:bg-yellow-200 transition disabled:opacity-50 animate-attention-pulse-yellow motion-reduce:animate-none disabled:animate-none"
            >
              {approvingAll ? '...' : 'Approve All'}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
          {getConfidenceIndicator()}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-xs text-red-800">{error}</p>
        </div>
      )}

      {/* Reopen action - show when completed, user has permission, and no accepted proposal */}
      {status === 'completed' && can.completeQuestionnaire() && (latestProposalStatus === null || ['rejected', 'client_rejected', 'sent_back'].includes(latestProposalStatus)) && (
        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
          <div className="text-xs text-gray-600">
            Reopen to update with new meetings, notes, or manual edits
          </div>
          <button
            onClick={() => setShowReopenModal(true)}
            className="px-4 py-1.5 rounded-lg text-sm font-medium transition bg-primary text-white hover:bg-primary-600"
          >
            Reopen Questionnaire
          </button>
        </div>
      )}

      {/* Actions - hide when already completed or for read-only roles */}
      {status !== 'completed' && can.completeQuestionnaire() && (
        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
          <div className="text-xs text-gray-600">
            {can_complete ? (
              <span className="text-green-600 font-medium">✓ Ready for completion</span>
            ) : (
              <span>Complete all required questions to finish</span>
            )}
          </div>
          <button
            onClick={() => setShowCompleteConfirm(true)}
            disabled={!can_complete || marking}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
              can_complete && !marking
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {marking ? (
              <>
                <span className="inline-block animate-spin mr-2">⟳</span>
                Marking Complete...
              </>
            ) : (
              'Mark as Complete'
            )}
          </button>
        </div>
      )}
      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={showApproveAllConfirm}
        onClose={() => setShowApproveAllConfirm(false)}
        onConfirm={handleApproveAll}
        title="Approve All Reviews"
        message={`Approve all ${needs_review_count} question${needs_review_count !== 1 ? 's' : ''} marked for review? This will clear the review flags so you can mark the questionnaire as complete.`}
        confirmText="Approve All"
        cancelText="Cancel"
        confirmVariant="primary"
        isLoading={approvingAll}
      />

      <ConfirmationModal
        isOpen={showCompleteConfirm}
        onClose={() => setShowCompleteConfirm(false)}
        onConfirm={handleMarkComplete}
        title="Mark Questionnaire Complete"
        message="Are you sure you want to mark this questionnaire as complete? This action finalizes all responses."
        confirmText="Mark Complete"
        cancelText="Cancel"
        confirmVariant="primary"
        isLoading={marking}
      />

      <ChangeProposalPanel
        leadId={leadId}
        isOpen={showProposalPanel}
        onClose={() => setShowProposalPanel(false)}
        onRefresh={onRefresh}
        isCompleted={status === 'completed'}
      />

      <ReopenQuestionnaireModal
        isOpen={showReopenModal}
        leadId={leadId}
        onClose={() => setShowReopenModal(false)}
        onRefresh={onRefresh}
      />
    </div>
  );
};

export default CompletionDashboard;
